import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import twilioClient from '../../utils/twilioClient';

const { FiCheck, FiX, FiMessageSquare, FiPhone, FiRefreshCw } = FiIcons;

const TwilioDashboard = () => {
  const [status, setStatus] = useState({ loading: true, data: null, error: null });
  const [messages, setMessages] = useState({ loading: true, data: [], error: null });
  const [calls, setCalls] = useState({ loading: true, data: [], error: null });

  const loadData = async () => {
    // Load status
    try {
      const statusData = await twilioClient.getStatus();
      setStatus({ loading: false, data: statusData, error: null });
    } catch (error) {
      setStatus({ loading: false, data: null, error: error.message });
    }

    // Load today's messages
    try {
      const today = new Date().toISOString().split('T')[0];
      const messagesData = await twilioClient.getMessages({ dateCreated: today });
      setMessages({ loading: false, data: messagesData.messages || [], error: null });
    } catch (error) {
      setMessages({ loading: false, data: [], error: error.message });
    }

    // Load today's calls
    try {
      const today = new Date().toISOString().split('T')[0];
      const callsData = await twilioClient.getCalls({ dateCreated: today });
      setCalls({ loading: false, data: callsData.calls || [], error: null });
    } catch (error) {
      setCalls({ loading: false, data: [], error: error.message });
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const retryConnection = async () => {
    setStatus({ ...status, loading: true });
    try {
      const statusData = await twilioClient.getStatus();
      setStatus({ loading: false, data: statusData, error: null });
    } catch (error) {
      setStatus({ loading: false, data: null, error: error.message });
    }
  };

  const getMessageCounts = () => {
    const inbound = messages.data.filter(m => m.direction === 'inbound').length;
    const outbound = messages.data.filter(m => m.direction === 'outbound-api').length;
    return { inbound, outbound };
  };

  const getCallCounts = () => {
    const answered = calls.data.filter(c => c.status === 'completed').length;
    const noAnswer = calls.data.filter(c => c.status === 'no-answer').length;
    const inProgress = calls.data.filter(c => c.status === 'in-progress').length;
    return { answered, noAnswer, inProgress };
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Connection Status Card */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Connection Status</h3>
            <button
              onClick={retryConnection}
              disabled={status.loading}
              className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <SafeIcon icon={FiRefreshCw} className={status.loading ? 'animate-spin' : ''} />
            </button>
          </div>
          
          {status.loading ? (
            <div className="flex items-center text-gray-500">
              <SafeIcon icon={FiRefreshCw} className="animate-spin mr-2" />
              Checking connection...
            </div>
          ) : status.error ? (
            <div className="flex items-center text-red-600">
              <SafeIcon icon={FiX} className="mr-2" />
              Not Connected
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center text-green-600">
                <SafeIcon icon={FiCheck} className="mr-2" />
                Connected
              </div>
              <div className="text-sm text-gray-600">
                <div>Account: {status.data.friendlyName}</div>
                <div>Status: {status.data.status}</div>
              </div>
            </div>
          )}
        </div>

        {/* Today's Messages */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center mb-4">
            <SafeIcon icon={FiMessageSquare} className="mr-2 text-blue-500" />
            <h3 className="text-lg font-medium text-gray-900">Today's Messages</h3>
          </div>
          
          {messages.loading ? (
            <div className="text-gray-500">Loading...</div>
          ) : messages.error ? (
            <div className="text-red-600 text-sm">Error: {messages.error}</div>
          ) : (
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Inbound:</span>
                <span className="font-medium">{getMessageCounts().inbound}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Outbound:</span>
                <span className="font-medium">{getMessageCounts().outbound}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-sm font-medium">Total:</span>
                <span className="font-medium">{messages.data.length}</span>
              </div>
            </div>
          )}
        </div>

        {/* Today's Calls */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center mb-4">
            <SafeIcon icon={FiPhone} className="mr-2 text-green-500" />
            <h3 className="text-lg font-medium text-gray-900">Today's Calls</h3>
          </div>
          
          {calls.loading ? (
            <div className="text-gray-500">Loading...</div>
          ) : calls.error ? (
            <div className="text-red-600 text-sm">Error: {calls.error}</div>
          ) : (
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Answered:</span>
                <span className="font-medium text-green-600">{getCallCounts().answered}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">No Answer:</span>
                <span className="font-medium text-yellow-600">{getCallCounts().noAnswer}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">In Progress:</span>
                <span className="font-medium text-blue-600">{getCallCounts().inProgress}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-sm font-medium">Total:</span>
                <span className="font-medium">{calls.data.length}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TwilioDashboard;